import { useEffect, useRef } from 'react';
import { reportPdfJobsApi, reportDeliveriesApi } from '../../../services/api';
import { useDataTransfer } from '../../../context/DataTransferContext';
import { useNotifications } from '../../../context/NotificationContext';
import { useAuth } from '../../../context/AuthContext';

// Server-built PDFs (reports over the browser's page ceiling). A job lives on the server, not in
// the tab that asked for it, so following one is app-level: the viewer starts a job and follows
// it, and ServerPdfJobResumer picks up any job no open tab is following (the asking tab was
// closed or reloaded) and saves the file when it is ready.

const POLL_MS = 3000;
const RESUME_CHECK_MS = 30000;
// One tab follows a job. A follower refreshes its claim every poll; a claim older than this is a
// tab that went away, and another tab may take the job over.
const CLAIM_STALE_MS = 15000;

const claimKey = (id) => `dawn.pdfJob.${id}`;

const readClaim = (id) => {
  try { return JSON.parse(localStorage.getItem(claimKey(id))); } catch { return null; }
};

const takeClaim = (id, sessionId) => {
  const c = readClaim(id);
  if (c && c.session !== sessionId && Date.now() - c.ts < CLAIM_STALE_MS) return false;
  try { localStorage.setItem(claimKey(id), JSON.stringify({ session: sessionId, ts: Date.now() })); } catch { /* storage off: follow anyway */ }
  return true;
};

const dropClaim = (id) => {
  try { localStorage.removeItem(claimKey(id)); } catch { /* nothing to drop */ }
};

// Jobs this tab is already following.
const following = new Set();

const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Follow a job to the end in Data Transfer and save its file. `dt` is the Data Transfer context.
 * Returns 'other-tab' when another open tab is already following it, otherwise when it ends.
 */
export async function followServerPdfJob(job, { dt, notify, exportRecipe }) {
  if (following.has(job.id)) return 'following';
  if (!takeClaim(job.id, dt.sessionId)) return 'other-tab';
  following.add(job.id);

  const actId = `pdf-${job.reportSlug}`;
  const label = job.activityName;
  const pages = job.pageCount || 0;
  // A server job takes no browser slot, but Data Transfer counts every download against its
  // concurrency limit and may queue this entry; a no-op restart lets the queue move past it.
  dt.registerActivity(actId, {
    name: label, type: 'download', format: 'pdf',
    phase: 'Queued on the server…', pct: 0,
    restartFn: () => {},
    exportRecipe,
  });

  const fail = (message) => {
    notify.clearStatus();
    dt.failActivity(actId);
    notify.error(`${label} - PDF - ${message}`);
  };

  try {
    const started = Date.now();
    const estSec = Math.max(30, pages * 1.1);
    let current = job;
    for (;;) {
      takeClaim(current.id, dt.sessionId);
      if (current.status === 'ready') break;
      if (current.status === 'failed') { fail(current.error || 'The server could not build the PDF.'); return 'failed'; }
      if (current.status === 'queued') {
        dt.updateActivity(actId, { phase: current.ahead ? `Queued on the server (${current.ahead} ahead)…` : 'Queued on the server…', pct: 0 });
      } else {
        const pct = Math.min(94, Math.round(((Date.now() - started) / 1000 / estSec) * 100));
        dt.updateActivity(actId, { phase: `Rendering ${pages.toLocaleString()} pages on the server…`, pct });
      }
      await new Promise(r => setTimeout(r, POLL_MS));
      try {
        current = (await reportPdfJobsApi.get(current.id)).data;
      } catch (err) {
        // A blip (a deploy restarting the API) is not a failure: the job survives it.
        if (err.response?.status === 404) { fail('The server build was not found.'); return 'failed'; }
      }
    }

    try {
      dt.updateActivity(actId, { phase: 'Downloading…', pct: 97 });
      const res = await reportDeliveriesApi.download(current.deliveryKey);
      saveBlob(res.data, current.fileName);
      notify.clearStatus();
      const mb = res.data?.size ? ` (${(res.data.size / 1048576).toFixed(1)} MB)` : '';
      notify.success(`${label} - PDF - Finished${mb}`, 5000);
      dt.completeActivity(actId);
      return 'saved';
    } catch (err) {
      // A blob response carries its JSON error as a Blob, so read it back as text.
      const text = await err.response?.data?.text?.().catch(() => null);
      let message = 'The download failed.';
      try { message = JSON.parse(text).error || message; } catch { /* keep the default */ }
      fail(message);
      return 'failed';
    }
  } finally {
    following.delete(job.id);
    dropClaim(job.id);
  }
}

/**
 * Mounted once at app level. On load, and every 30 s, asks the server for this person's
 * unfinished or unsaved PDF jobs and follows any that no open tab is following.
 */
export function ServerPdfJobResumer() {
  const dt = useDataTransfer();
  const { notify } = useNotifications();
  const { currentUser } = useAuth();
  const dtRef = useRef(dt);
  dtRef.current = dt;

  useEffect(() => {
    if (!currentUser?.id) return undefined;
    let stopped = false;
    const check = async () => {
      let jobs;
      try { jobs = (await reportPdfJobsApi.mine()).data || []; } catch { return; }
      for (const job of jobs) {
        if (stopped) return;
        if (following.has(job.id)) continue;
        const c = readClaim(job.id);
        if (c && c.session !== dtRef.current.sessionId && Date.now() - c.ts < CLAIM_STALE_MS) continue;
        notify.info(`${job.activityName} - PDF - picked up the server build`, 4000);
        followServerPdfJob(job, { dt: dtRef.current, notify });
      }
    };
    check();
    const timer = setInterval(check, RESUME_CHECK_MS);
    return () => { stopped = true; clearInterval(timer); };
  }, [currentUser?.id, notify]);

  return null;
}
